import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft, 
  MapPin, 
  User, 
  Phone, 
  Package, 
  ChevronRight,
  Heart,
  Plus,
  Check,
  AlertCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import LocationPicker from "../components/LocationPicker";
import axios from "axios";
import { getBackendUrl } from "../utils/api";

const API_URL = getBackendUrl();

const WEIGHT_CATEGORIES = [
  { id: "light", label: "Light", desc: "< 5 kg", icon: "📦" },
  { id: "medium", label: "Medium", desc: "5-10 kg", icon: "📦📦" },
  { id: "heavy", label: "Heavy", desc: "10-20 kg", icon: "📦📦📦" }
];

const PACKAGE_CONTENTS = [
  "Documents",
  "Food",
  "Medicine",
  "Books",
  "Keys",
  "Clothes",
  "Electronics",
  "Gifts",
  "Other"
];

const BANNED_ITEMS = ["Alcohol", "Illegal items", "Banned substances", "Weapons", "Flammable items"];

const FleetyGoBookingPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [step, setStep] = useState(() => {
    const savedStep = sessionStorage.getItem("fleetygo_step");
    return savedStep ? parseInt(savedStep) : 1;
  }); // 1: Pickup, 2: Drop, 3: Package, 4: Review
  const [showLocationPicker, setShowLocationPicker] = useState(null); // 'pickup' or 'drop'
  const [favoriteAddresses, setFavoriteAddresses] = useState([]);
  const [showFavorites, setShowFavorites] = useState(null);
  const [fare, setFare] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState("");
  const [savingFavorite, setSavingFavorite] = useState(null); // 'pickup' or 'drop'
  const [savedFavorite, setSavedFavorite] = useState(null); // 'pickup' or 'drop'

  const [pickup, setPickup] = useState(() => {
    const saved = sessionStorage.getItem("fleetygo_pickup");
    return saved ? JSON.parse(saved) : {
      name: "",
      phone: "",
      address: "",
      address_line2: "",
      lat: null,
      lng: null
    };
  });

  const [drop, setDrop] = useState(() => {
    const saved = sessionStorage.getItem("fleetygo_drop");
    return saved ? JSON.parse(saved) : {
      name: "",
      phone: "",
      address: "",
      address_line2: "",
      lat: null,
      lng: null
    };
  });

  const [packageDetails, setPackageDetails] = useState(() => {
    const saved = sessionStorage.getItem("fleetygo_package");
    return saved ? JSON.parse(saved) : {
      weight_category: "",
      contents: "",
      description: ""
    };
  });

  // Persist pickup data to sessionStorage
  useEffect(() => {
    if (pickup.name || pickup.address) {
      sessionStorage.setItem("fleetygo_pickup", JSON.stringify(pickup));
    }
  }, [pickup]);

  // Persist drop data to sessionStorage
  useEffect(() => {
    if (drop.name || drop.address) {
      sessionStorage.setItem("fleetygo_drop", JSON.stringify(drop));
    }
  }, [drop]);

  // Persist package details to sessionStorage
  useEffect(() => {
    if (packageDetails.weight_category || packageDetails.contents) {
      sessionStorage.setItem("fleetygo_package", JSON.stringify(packageDetails));
    }
  }, [packageDetails]);

  // Restore step from sessionStorage
  useEffect(() => {
    const savedStep = sessionStorage.getItem("fleetygo_step");
    if (savedStep) {
      setStep(parseInt(savedStep, 10));
      // Clear the saved step after reading (one-time use for repeat deliveries)
      sessionStorage.removeItem("fleetygo_step");
    }
  }, []);

  // Auto-calculate fare when landing on step 4 (e.g., from repeat delivery)
  useEffect(() => {
    if (step === 4 && !fare && pickup.lat && drop.lat) {
      calculateFare();
    }
  }, [step, pickup.lat, drop.lat, fare]);

  // Persist step to sessionStorage
  useEffect(() => {
    sessionStorage.setItem("fleetygo_step", step.toString());
  }, [step]);

  useEffect(() => {
    const userData = localStorage.getItem("fleety_user");
    if (!userData) {
      navigate("/login");
      return;
    }
    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);
    
    // Pre-fill sender details only if pickup doesn't have saved data
    const savedPickup = sessionStorage.getItem("fleetygo_pickup");
    if (!savedPickup) {
      setPickup(prev => ({
        ...prev,
        name: parsedUser.full_name,
        phone: parsedUser.phone_number
      }));
    }

    // Fetch favorite addresses
    fetchFavoriteAddresses(parsedUser.id);
  }, [navigate]);

  const fetchFavoriteAddresses = async (userId) => {
    try {
      const response = await axios.get(`${API_URL}/api/fleetygo/favorite-addresses/${userId}`);
      setFavoriteAddresses(response.data);
    } catch (error) {
      console.error("Error fetching favorites:", error);
    }
  };

  const handleLocationSelect = (location, type) => {
    const setter = type === "pickup" ? setPickup : setDrop;
    setter(prev => ({
      ...prev,
      address: location.address,
      lat: location.lat,
      lng: location.lng
    }));
    setShowLocationPicker(null);
  };

  const handleFavoriteSelect = (fav, type) => {
    const setter = type === "pickup" ? setPickup : setDrop;
    setter({
      name: fav.name,
      phone: fav.phone,
      address: fav.address,
      address_line2: fav.address_line2 || "",
      lat: fav.lat,
      lng: fav.lng
    });
    setShowFavorites(null);
  };

  const calculateFare = async () => {
    if (!pickup.lat || !drop.lat) return;
    
    setIsCalculating(true);
    try {
      const response = await axios.get(`${API_URL}/api/fleetygo/calculate-fare`, {
        params: {
          sender_lat: pickup.lat,
          sender_lng: pickup.lng,
          receiver_lat: drop.lat,
          receiver_lng: drop.lng
        }
      });
      setFare(response.data);
    } catch (error) {
      console.error("Error calculating fare:", error);
    }
    setIsCalculating(false);
  };

  useEffect(() => {
    if (pickup.lat && drop.lat) {
      calculateFare();
    }
  }, [pickup.lat, drop.lat]);

  const validateStep = (stepNum) => {
    setError("");
    
    if (stepNum === 1) {
      if (!pickup.name.trim()) { setError("Please enter sender's name"); return false; }
      if (!pickup.phone || pickup.phone.length !== 10) { setError("Please enter valid 10-digit phone"); return false; }
      if (!pickup.lat) { setError("Please select pickup location on map"); return false; }
      if (!pickup.address_line2.trim()) { setError("Please enter flat/building details"); return false; }
    }
    
    if (stepNum === 2) {
      if (!drop.name.trim()) { setError("Please enter receiver's name"); return false; }
      if (!drop.phone || drop.phone.length !== 10) { setError("Please enter valid 10-digit phone"); return false; }
      if (!drop.lat) { setError("Please select drop location on map"); return false; }
      if (!drop.address_line2.trim()) { setError("Please enter flat/building details"); return false; }
    }
    
    if (stepNum === 3) {
      if (!packageDetails.weight_category) { setError("Please select weight category"); return false; }
      if (!packageDetails.contents) { setError("Please select package contents"); return false; }
    }
    
    return true;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  // Clear all booking data from sessionStorage
  const clearBookingData = () => {
    sessionStorage.removeItem("fleetygo_pickup");
    sessionStorage.removeItem("fleetygo_drop");
    sessionStorage.removeItem("fleetygo_package");
    sessionStorage.removeItem("fleetygo_step");
  };

  const handleProceedToPayment = () => {
    if (!validateStep(3)) return;
    
    // Store booking data in session
    const bookingData = {
      pickup,
      drop,
      packageDetails,
      fare,
      user_id: user.id
    };
    sessionStorage.setItem("fleetygo_booking", JSON.stringify(bookingData));
    
    // Clear individual field data (not needed after proceeding to payment)
    clearBookingData();
    
    navigate("/fleetygo/payment");
  };

  const saveFavorite = async (type) => {
    const data = type === "pickup" ? pickup : drop;
    setSavingFavorite(type);
    setSavedFavorite(null);
    try {
      await axios.post(`${API_URL}/api/fleetygo/favorite-addresses`, {
        user_id: user.id,
        label: type === "pickup" ? "Pickup" : "Drop",
        name: data.name,
        phone: data.phone,
        address: data.address,
        address_line2: data.address_line2,
        lat: data.lat,
        lng: data.lng
      });
      fetchFavoriteAddresses(user.id);
      setSavedFavorite(type);
      // Auto-hide success message after 3 seconds
      setTimeout(() => setSavedFavorite(null), 3000);
    } catch (error) {
      console.error("Error saving favorite:", error);
      setError("Failed to save address to favorites. Please try again.");
    } finally {
      setSavingFavorite(null);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="fleetygo-booking">
      {/* Header */}
      <header className="bg-[#3FA9F5] p-4 flex items-center gap-4 sticky top-0 z-10">
        <button onClick={() => step > 1 ? setStep(step - 1) : navigate("/fleetygo")} className="text-white">
          <ArrowLeft size={24} />
        </button>
        <h1 className="text-white text-lg font-semibold">
          {step === 1 && "Pickup Details"}
          {step === 2 && "Drop Details"}
          {step === 3 && "Package Details"}
          {step === 4 && "Review & Confirm"}
        </h1>
      </header>

      {/* Progress Bar */}
      <div className="bg-white px-4 py-3 border-b">
        <div className="flex items-center justify-between">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= s ? "bg-[#3FA9F5] text-white" : "bg-gray-200 text-gray-500"
              }`}>
                {step > s ? <Check size={16} /> : s}
              </div>
              {s < 4 && (
                <div className={`w-12 h-1 mx-1 ${step > s ? "bg-[#3FA9F5]" : "bg-gray-200"}`} />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-1 text-xs text-gray-500">
          <span>Pickup</span>
          <span>Drop</span>
          <span>Package</span>
          <span>Review</span>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 p-4 overflow-y-auto">
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600 text-sm">
            <AlertCircle size={18} />
            {error}
          </div>
        )}

        {/* Step 1: Pickup Details */}
        {step === 1 && (
          <div className="space-y-4">
            {/* Favorites */}
            {favoriteAddresses.length > 0 && (
              <div className="relative">
                <button
                  onClick={() => setShowFavorites(showFavorites === "pickup" ? null : "pickup")}
                  className="w-full flex items-center justify-between p-3 bg-[#3FA9F5]/5 rounded-lg text-[#3FA9F5] font-medium text-sm"
                >
                  <span className="flex items-center gap-2">
                    <Heart size={18} />
                    Choose from Favorites
                  </span>
                  <ChevronRight size={18} className={showFavorites === "pickup" ? "rotate-90" : ""} />
                </button>
                {showFavorites === "pickup" && (
                  <div className="mt-2 bg-white rounded-lg border shadow-sm max-h-40 overflow-y-auto">
                    {favoriteAddresses.map((fav) => (
                      <button
                        key={fav.id}
                        onClick={() => handleFavoriteSelect(fav, "pickup")}
                        className="w-full p-3 text-left hover:bg-gray-50 border-b last:border-b-0"
                      >
                        <p className="font-medium text-sm text-gray-900">{fav.label} - {fav.name}</p>
                        <p className="text-xs text-gray-500 truncate">{fav.address}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Sender Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <User size={16} className="inline mr-1" />
                Sender's Name *
              </label>
              <Input
                value={pickup.name}
                onChange={(e) => setPickup({ ...pickup, name: e.target.value })}
                placeholder="Enter sender's name"
              />
            </div>

            {/* Sender Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Phone size={16} className="inline mr-1" />
                Sender's Phone *
              </label>
              <Input
                value={pickup.phone}
                onChange={(e) => setPickup({ ...pickup, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                placeholder="10-digit phone number"
                type="tel"
              />
            </div>

            {/* Pickup Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <MapPin size={16} className="inline mr-1" />
                Pickup Location *
              </label>
              <button
                onClick={() => setShowLocationPicker("pickup")}
                className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-left hover:border-[#3FA9F5] transition-colors"
              >
                {pickup.address ? (
                  <p className="text-sm text-gray-700">{pickup.address}</p>
                ) : (
                  <p className="text-sm text-gray-400">Tap to select location on map</p>
                )}
              </button>
            </div>

            {/* Address Line 2 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Flat/Building/Floor *
              </label>
              <Input
                value={pickup.address_line2}
                onChange={(e) => setPickup({ ...pickup, address_line2: e.target.value })}
                placeholder="Flat no, Building name, Floor"
              />
            </div>

            {/* Save to Favorites */}
            {pickup.lat && pickup.name && (
              <button
                onClick={() => saveFavorite("pickup")}
                disabled={savingFavorite === "pickup"}
                className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                  savedFavorite === "pickup" 
                    ? "text-green-600" 
                    : savingFavorite === "pickup"
                    ? "text-gray-400"
                    : "text-[#3FA9F5] hover:text-[#2196F3]"
                }`}
              >
                {savingFavorite === "pickup" ? (
                  <>
                    <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </>
                ) : savedFavorite === "pickup" ? (
                  <>
                    <Check size={16} className="text-green-600" />
                    Saved to Favorites!
                  </>
                ) : (
                  <>
                    <Heart size={16} />
                    Save to Favorites
                  </>
                )}
              </button>
            )}
          </div>
        )}

        {/* Step 2: Drop Details */}
        {step === 2 && (
          <div className="space-y-4">
            {/* Favorites */}
            {favoriteAddresses.length > 0 && (
              <div className="relative">
                <button
                  onClick={() => setShowFavorites(showFavorites === "drop" ? null : "drop")}
                  className="w-full flex items-center justify-between p-3 bg-[#3FA9F5]/5 rounded-lg text-[#3FA9F5] font-medium text-sm"
                >
                  <span className="flex items-center gap-2">
                    <Heart size={18} />
                    Choose from Favorites
                  </span>
                  <ChevronRight size={18} className={showFavorites === "drop" ? "rotate-90" : ""} />
                </button>
                {showFavorites === "drop" && (
                  <div className="mt-2 bg-white rounded-lg border shadow-sm max-h-40 overflow-y-auto">
                    {favoriteAddresses.map((fav) => (
                      <button
                        key={fav.id}
                        onClick={() => handleFavoriteSelect(fav, "drop")}
                        className="w-full p-3 text-left hover:bg-gray-50 border-b last:border-b-0"
                      >
                        <p className="font-medium text-sm text-gray-900">{fav.label} - {fav.name}</p>
                        <p className="text-xs text-gray-500 truncate">{fav.address}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Receiver Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <User size={16} className="inline mr-1" />
                Receiver's Name *
              </label>
              <Input
                value={drop.name}
                onChange={(e) => setDrop({ ...drop, name: e.target.value })}
                placeholder="Enter receiver's name"
              />
            </div>

            {/* Receiver Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Phone size={16} className="inline mr-1" />
                Receiver's Phone *
              </label>
              <Input
                value={drop.phone}
                onChange={(e) => setDrop({ ...drop, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                placeholder="10-digit phone number"
                type="tel"
              />
            </div>

            {/* Drop Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <MapPin size={16} className="inline mr-1" />
                Drop Location *
              </label>
              <button
                onClick={() => setShowLocationPicker("drop")}
                className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-left hover:border-[#3FA9F5] transition-colors"
              >
                {drop.address ? (
                  <p className="text-sm text-gray-700">{drop.address}</p>
                ) : (
                  <p className="text-sm text-gray-400">Tap to select location on map</p>
                )}
              </button>
            </div>

            {/* Address Line 2 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Flat/Building/Floor *
              </label>
              <Input
                value={drop.address_line2}
                onChange={(e) => setDrop({ ...drop, address_line2: e.target.value })}
                placeholder="Flat no, Building name, Floor"
              />
            </div>

            {/* Save to Favorites */}
            {drop.lat && drop.name && (
              <button
                onClick={() => saveFavorite("drop")}
                disabled={savingFavorite === "drop"}
                className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                  savedFavorite === "drop" 
                    ? "text-green-600" 
                    : savingFavorite === "drop"
                    ? "text-gray-400"
                    : "text-[#3FA9F5] hover:text-[#2196F3]"
                }`}
              >
                {savingFavorite === "drop" ? (
                  <>
                    <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </>
                ) : savedFavorite === "drop" ? (
                  <>
                    <Check size={16} className="text-green-600" />
                    Saved to Favorites!
                  </>
                ) : (
                  <>
                    <Heart size={16} />
                    Save to Favorites
                  </>
                )}
              </button>
            )}
          </div>
        )}

        {/* Step 3: Package Details */}
        {step === 3 && (
          <div className="space-y-4">
            {/* Weight Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Package size={16} className="inline mr-1" />
                Package Weight *
              </label>
              <div className="grid grid-cols-3 gap-2">
                {WEIGHT_CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setPackageDetails({ ...packageDetails, weight_category: cat.id })}
                    className={`p-3 rounded-lg border-2 text-center transition-colors ${
                      packageDetails.weight_category === cat.id
                        ? "border-[#3FA9F5] bg-[#3FA9F5]/5"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <span className="text-2xl">{cat.icon}</span>
                    <p className="font-medium text-sm mt-1">{cat.label}</p>
                    <p className="text-xs text-gray-500">{cat.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Package Contents */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                What's inside? *
              </label>
              <div className="flex flex-wrap gap-2">
                {PACKAGE_CONTENTS.map((item) => (
                  <button
                    key={item}
                    onClick={() => setPackageDetails({ ...packageDetails, contents: item })}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      packageDetails.contents === item
                        ? "bg-[#3FA9F5] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional Description (Optional)
              </label>
              <textarea
                value={packageDetails.description}
                onChange={(e) => setPackageDetails({ ...packageDetails, description: e.target.value })}
                placeholder="Any special instructions or item details..."
                className="w-full p-3 border rounded-lg text-sm resize-none"
                rows={3}
              />
            </div>

            {/* Banned Items Warning */}
            <div className="p-3 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm font-medium text-red-600 mb-1">⚠️ Items NOT allowed:</p>
              <p className="text-xs text-red-500">{BANNED_ITEMS.join(" • ")}</p>
            </div>
          </div>
        )}

        {/* Step 4: Review */}
        {step === 4 && (
          <div className="space-y-4">
            {/* Pickup Summary */}
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-3 h-3 bg-green-500 rounded-full" />
                <h3 className="font-semibold text-gray-900">Pickup</h3>
              </div>
              <p className="font-medium text-sm">{pickup.name} • {pickup.phone}</p>
              <p className="text-sm text-gray-600">{pickup.address}</p>
              <p className="text-sm text-gray-500">{pickup.address_line2}</p>
            </div>

            {/* Drop Summary */}
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-3 h-3 bg-red-500 rounded-full" />
                <h3 className="font-semibold text-gray-900">Drop</h3>
              </div>
              <p className="font-medium text-sm">{drop.name} • {drop.phone}</p>
              <p className="text-sm text-gray-600">{drop.address}</p>
              <p className="text-sm text-gray-500">{drop.address_line2}</p>
            </div>

            {/* Package Summary */}
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-3">Package Details</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Weight</span>
                  <span className="font-medium">{WEIGHT_CATEGORIES.find(c => c.id === packageDetails.weight_category)?.label}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Contents</span>
                  <span className="font-medium">{packageDetails.contents}</span>
                </div>
                {packageDetails.description && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Note</span>
                    <span className="font-medium">{packageDetails.description}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Fare Summary */}
            {fare && (
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-3">Fare Breakdown</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Distance</span>
                    <span className="font-medium">{fare.distance_km} km</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Delivery Charge</span>
                    <span className="font-medium">₹{fare.delivery_charge}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Platform Fee</span>
                    <span className="font-medium">₹{fare.platform_fee}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">GST (18%)</span>
                    <span className="font-medium">₹{fare.gst}</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between">
                    <span className="font-semibold text-gray-900">Total</span>
                    <span className="font-bold text-[#3FA9F5] text-lg">₹{fare.total}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Bottom CTA */}
      <div className="p-4 bg-white border-t">
        {step < 4 ? (
          <Button
            onClick={handleNext}
            className="w-full bg-[#3FA9F5] hover:bg-[#3FA9F5]/90 text-white py-3 rounded-xl font-semibold"
          >
            Continue
          </Button>
        ) : (
          <Button
            onClick={handleProceedToPayment}
            disabled={!fare}
            className="w-full bg-[#3FA9F5] hover:bg-[#3FA9F5]/90 text-white py-3 rounded-xl font-semibold"
          >
            Proceed to Payment • ₹{fare?.total || 0}
          </Button>
        )}
      </div>

      {/* Location Picker Modal */}
      {showLocationPicker && (
        <LocationPicker
          isOpen={true}
          onClose={() => setShowLocationPicker(null)}
          onLocationSelect={(location) => handleLocationSelect(location, showLocationPicker)}
          initialLocation={showLocationPicker === "pickup" ? 
            (pickup.lat ? { lat: pickup.lat, lng: pickup.lng } : null) :
            (drop.lat ? { lat: drop.lat, lng: drop.lng } : null)
          }
          title={showLocationPicker === "pickup" ? "Select Pickup Location" : "Select Drop Location"}
          locationLabel={showLocationPicker === "pickup" ? "Picking package up from" : "Delivering package to"}
        />
      )}
    </div>
  );
};

export default FleetyGoBookingPage;
